import DisposalType from 'owa-service/lib/contract/DisposalType';
import distinguishedFolderId from 'owa-service/lib/factory/distinguishedFolderId';
import emptyFolderOperation from 'owa-service/lib/operation/emptyFolderOperation';
import emptyFolderRequest from 'owa-service/lib/factory/emptyFolderRequest';
import Folder from 'owa-service/lib/contract/Folder';
import folderId from 'owa-service/lib/factory/folderId';
import folderIdToName from 'owa-session-store/lib/utils/folderIdToName';
import getUserConfiguration from 'owa-session-store/lib/actions/getUserConfiguration';
import itemId from 'owa-service/lib/factory/itemId';
import SingleResponseMessage from 'owa-service/lib/contract/SingleResponseMessage';
import { BulkActionCustomDataBase, lazyfindBulkActionItemAction } from 'owa-bulk-action-store';
import { getJsonRequestHeader } from 'owa-service/lib/ServiceRequestUtils';
import { isFeatureEnabled } from 'owa-feature-flags';
import { isFolderOfTreeType } from 'owa-folders';
import { SHARED_FOLDERS_TREE_TYPE } from 'owa-folders-constants';
import { OwaDate, getISOString } from 'owa-datetime';

/**
 * Create and submit the empty folder action for the specified folder
 * @param folder the folder to be emptied
 * @param disposalType the disposal type
 * @param deleteSubFolder whether to delete subfolder
 * @param clientLastSyncTime the client last sync time watermark
 * @param itemIdsToExclude item ids to exclude from empty folder operation
 * @param shouldSupportBulkActions flag indicating whether the bulk actions is supported for this empty folder request
 */
export function emptyFolderService(
    folder: Folder,
    disposalType: DisposalType,
    deleteSubFolder: boolean,
    clientLastSyncTime: OwaDate | null | undefined,
    itemIdsToExclude: string[],
    shouldSupportBulkActions: boolean,
    bulkActionCustomData?: BulkActionCustomDataBase
): Promise<SingleResponseMessage> {
    const requestBody = configureRequestBody(
        folder,
        disposalType,
        deleteSubFolder,
        clientLastSyncTime,
        itemIdsToExclude,
        shouldSupportBulkActions,
        bulkActionCustomData
    );
    return emptyFolderOperation({
        Header: getJsonRequestHeader(),
        Body: requestBody,
    }).then(response => {
        lazyfindBulkActionItemAction.importAndExecute(); // Need this to update Bulk Action Store when Notification Channel is not set up.
        return response.Body.ResponseMessages.Items[0];
    });
}

/**
 * Configure the empty folder request body
 * @param folder the folder to be emptied
 * @param disposalType the disposal type
 * @param deleteSubFolder whether to delete subfolder
 * @param clientLastSyncTime the client last sync time watermark
 * @param itemIdsToExclude item ids to exclude from empty folder operation
 * @param shouldSupportBulkActions flag indicating whether the bulk actions is supported for this empty folder request
 * @param bulkActionCustomData custom data to be sent down to BulkActionItem
 */
function configureRequestBody(
    folder: Folder,
    disposalType: DisposalType,
    deleteSubFolder: boolean,
    clientLastSyncTime: OwaDate | null | undefined,
    itemIdsToExclude: string[],
    shouldSupportBulkActions: boolean,
    bulkActionCustomData?: BulkActionCustomDataBase
) {
    let folderIdToEmpty = null;

    const distinguishedFolderIdName = folderIdToName(folder.FolderId.Id);
    folderIdToEmpty =
        distinguishedFolderIdName == 'none'
            ? folderId({ Id: folder.FolderId.Id })
            : distinguishedFolderId({ Id: distinguishedFolderIdName });
    const baseItemIds =
        itemIdsToExclude != undefined ? itemIdsToExclude.map(id => itemId({ Id: id })) : undefined;

    const body = emptyFolderRequest({
        FolderIds: [folderIdToEmpty],
        AllowSearchFolder: true,
        DeleteType: disposalType,
        DeleteSubFolders: deleteSubFolder,
        SuppressReadReceipt:
            getUserConfiguration().UserOptions.ReadReceipt != 'AlwaysSend' ||
            isFolderOfTreeType(folder.FolderId.Id, SHARED_FOLDERS_TREE_TYPE),
        ItemIdsToExclude: baseItemIds,
        BulkActionRequest: shouldSupportBulkActions,
        BulkActionCustomData: bulkActionCustomData && JSON.stringify(bulkActionCustomData),
        BulkActionBatchSize: isFeatureEnabled('tri-emptyFolderBulkActionBatchSize') ? 200 : 10,
    });

    // Server does not support specify ClientLastSyncTime and deleting subfolders at the same time
    if (deleteSubFolder && clientLastSyncTime) {
        throw new Error('ClientLastSyncTime is not supported when deleting subfolders.');
    }

    // Specify ClientLastSyncTime if not delete sub folders
    if (!deleteSubFolder) {
        body.ClientLastSyncTime = clientLastSyncTime && getISOString(clientLastSyncTime);
    }

    return body;
}
